import { Stack } from '@mantine/core';
import { useState } from 'react';
import type { EventListItem } from './groupEvents';
import type { TimelineEventView } from './timelineEvent';
import { TimelineEventCard } from './TimelineEventCard';
import { TimelineEventGroup } from './TimelineEventGroup';

export interface TimelineEventListProps<V extends TimelineEventView = TimelineEventView> {
  /** Output of `groupEvents()` — singles and groups, newest first. */
  items: EventListItem<V>[];
  onEventClick?: (event: V) => void;
}

/** The Timeline page's event list: a card per single event and a
 *  collapsible group per burst. Groups start EXPANDED; the list remembers the
 *  ones the user collapsed by their stable key, so a live event joining a group
 *  leaves it as it was. */
export function TimelineEventList<V extends TimelineEventView = TimelineEventView>({
  items,
  onEventClick,
}: TimelineEventListProps<V>) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggleGroup = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Stack miw={0} gap={12}>
      {items.map((item) =>
        item.kind === 'single' ? (
          <TimelineEventCard key={item.key} event={item.event} onClick={onEventClick} />
        ) : (
          <TimelineEventGroup
            key={item.key}
            group={item}
            expanded={!collapsed.has(item.key)}
            onToggle={() => toggleGroup(item.key)}
            onEventClick={onEventClick}
          />
        ),
      )}
    </Stack>
  );
}
